import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

interface ImagePopoverProps {
  imageData: string;
  alt: string;
  children: React.ReactNode;
}

const POPOVER_ESTIMATED_HEIGHT = 220;

const ImagePopover: React.FC<ImagePopoverProps> = ({
  imageData,
  alt,
  children,
}) => {
  const [showPopover, setShowPopover] = useState(false);

  const [popoverPosition, setPopoverPosition] = useState({
    top: 0,
    left: 0,
    transform: "translate(-50%, 0)",
  });
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - rect.bottom;

      let topPosition: number;
      let transformStyle: string;

      if (spaceBelow >= POPOVER_ESTIMATED_HEIGHT + 10) {
        topPosition = rect.bottom + window.scrollY;
        transformStyle = "translate(-50%, 10px)";
      } else {
        topPosition = rect.top + window.scrollY;
        transformStyle = "translate(-50%, calc(-100% - 10px))";
      }

      setPopoverPosition({
        top: topPosition,
        left: rect.left + rect.width / 2 + window.scrollX,
        transform: transformStyle,
      });
    }
    setShowPopover(true);
  };

  const handleMouseLeave = () => {
    setShowPopover(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowPopover(false);
    };
    if (showPopover) {
      window.addEventListener("scroll", handleScroll, true);
    }
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [showPopover]);

  return (
    <div
      className="cursor-pointer"
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {showPopover &&
        ReactDOM.createPortal(
          <div
            className="absolute z-50 p-2 bg-white border border-gray-300 rounded-md shadow-lg"
            style={{
              position: "absolute",
              top: `${popoverPosition.top}px`,
              left: `${popoverPosition.left}px`,
              transform: popoverPosition.transform,
              pointerEvents: "none",
            }}
          >
            <img
              src={imageData}
              alt={`${alt} - No data`}
              className="block object-cover w-48 h-48"
            />
          </div>,
          document.body
        )}
    </div>
  );
};

export default ImagePopover;
